import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, StatusBadge } from './ui';

export type GuideActionTarget =
  | 'batch-management'
  | 'batch-title-settings'
  | 'create-batch-form';

export interface GuideStep {
  id: number | string;
  title: string;
  summary: string;
  bullets: string[];
  exampleTitle: string;
  examplePoints: string[];
  exampleCode?: string;
  primaryActionLabel?: string;
  primaryActionPath?: string;
  primaryActionTarget?: GuideActionTarget;
  secondaryActionLabel?: string;
  secondaryActionPath?: string;
  visual?: ReactNode;
}

interface GuideActionHandlers {
  onOpenBatchManagement?: () => void;
  onOpenBatchTitleSettings?: () => void;
  onOpenCreateBatchForm?: () => void;
}

interface BatchCreationGuideSectionProps extends GuideActionHandlers {
  className?: string;
}

interface BatchCreationStepHelpProps extends GuideActionHandlers {
  step: GuideStep;
  className?: string;
  defaultExpanded?: boolean;
}

function GuideVisualFrame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        </div>
        <span className="text-xs font-medium text-slate-600">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function BatchTitleCodesVisual() {
  return (
    <GuideVisualFrame title="Batch Title Settings">
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center text-xs font-semibold">
          <div className="rounded-lg bg-blue-50 px-2 py-2 text-blue-700">Product Code</div>
          <div className="rounded-lg bg-emerald-50 px-2 py-2 text-emerald-700">Unit Code</div>
          <div className="rounded-lg bg-indigo-50 px-2 py-2 text-indigo-700">Process Ver.</div>
        </div>
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-center text-xs text-slate-600">
          <span className="font-semibold text-slate-800">BT = Product + Unit + Process + Date</span>
        </div>
        <div className="space-y-2">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      </div>
    </GuideVisualFrame>
  );
}

function CreateBatchVisual() {
  return (
    <GuideVisualFrame title="Create Product Batch Form">
      <div className="space-y-2.5">
        <div className="h-8 rounded-md bg-slate-100" />
        <div className="grid grid-cols-2 gap-2">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
        <div className="h-8 rounded-md bg-slate-100" />
        <div className="grid grid-cols-2 gap-2">
          <div className="h-8 rounded-md border border-blue-300 bg-blue-50" />
          <div className="h-8 rounded-md border border-blue-300 bg-blue-50" />
        </div>
        <div className="flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          <span>Total products in batch</span>
          <span className="font-semibold">1,000</span>
        </div>
      </div>
    </GuideVisualFrame>
  );
}

function EnhancedInfoVisual() {
  return (
    <GuideVisualFrame title="Enhanced Product Information">
      <div className="grid grid-cols-5 gap-2">
        <div className="col-span-2 space-y-2">
          <div className="h-14 rounded-md bg-slate-100" />
          <div className="h-14 rounded-md bg-slate-100" />
          <div className="h-14 rounded-md bg-slate-100" />
        </div>
        <div className="col-span-3 space-y-2">
          <div className="h-8 rounded-md bg-amber-50" />
          <div className="h-8 rounded-md bg-amber-50" />
          <div className="h-8 rounded-md bg-amber-50" />
          <div className="h-8 rounded-md bg-amber-50" />
        </div>
      </div>
    </GuideVisualFrame>
  );
}

function CreditsVisual() {
  return (
    <GuideVisualFrame title="Cost & Credit Validation">
      <div className="space-y-3">
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <p className="text-xs text-blue-700">Batch Creation Cost</p>
          <p className="text-lg font-semibold text-blue-900">INR 1,450</p>
        </div>
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
          <p className="text-xs text-emerald-700">Available Credits</p>
          <p className="text-lg font-semibold text-emerald-900">INR 8,000</p>
        </div>
        <div className="rounded-md bg-emerald-100 px-3 py-2 text-xs font-medium text-emerald-800">
          Sufficient credits available
        </div>
      </div>
    </GuideVisualFrame>
  );
}

function PostCreationVisual() {
  return (
    <GuideVisualFrame title="After Batch Creation">
      <div className="space-y-2.5">
        <div className="h-9 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700">
          Download All QR Codes
        </div>
        <div className="h-9 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700">
          Print Pack (A4 / Letter)
        </div>
        <div className="h-9 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700">
          Thermal Labels
        </div>
        <div className="grid grid-cols-4 gap-2 pt-1">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="h-6 rounded bg-slate-900" />
          ))}
        </div>
      </div>
    </GuideVisualFrame>
  );
}

export const batchCreationGuideSteps: GuideStep[] = [
  {
    id: 1,
    title: 'Set Up Batch Title Codes',
    summary:
      'Define product, unit, and process code dictionaries used to build consistent batch titles.',
    bullets: [
      'Open Batch Title Codes and add your product code list.',
      'Add unit codes (for example: 250G, 500ML, 1L) that match your packaging sizes.',
      'Add process versions for recipe or line revisions to preserve traceability.',
    ],
    exampleTitle: 'Example: Batch title code construction',
    examplePoints: [
      'Product code: GHEE01 | Unit code: 500ML | Process version: R2',
      'Batch date: 2026-02-21 becomes date segment 20260221',
      'First batch on same date/code/unit: sequence B01',
      'Second batch on same date/code/unit: sequence B02',
      'Process version is optional. If omitted, title ends after unit code.',
    ],
    exampleCode:
      'GHEE01-20260221-B01-500ML-R2\nGHEE01-20260221-B02-500ML-R2\nGHEE01-20260221-B01-500ML',
    primaryActionLabel: 'Open Batch Title Codes',
    primaryActionTarget: 'batch-title-settings',
    visual: <BatchTitleCodesVisual />,
  },
  {
    id: 2,
    title: 'Create the Product Batch',
    summary:
      'Use Create Product Batch to enter product details and serial number range for QR generation.',
    bullets: [
      'Choose product type early so fresh-produce batches use the right downstream fields.',
      'Enter product name, product code, unit code, process version, and batch date.',
      'Set serial start and serial end. System calculates total product count automatically.',
      'Keep inner QR enabled if you need post-purchase consumption marking.',
    ],
    exampleTitle: 'Example: Batch creation input',
    examplePoints: [
      'Product name: Premium Cow Ghee 500ml',
      'Product code/unit/process/date: GHEE01 / 500ML / R2 / 2026-02-21',
      'Serial start/end: 1001 to 2000 (total 1,000 products)',
      'Inner QR enabled -> pre-purchase and post-purchase workflow supported',
    ],
    exampleCode: 'Serial range: 1001-2000\nProduct count: 1000',
    primaryActionLabel: 'Open Create Product Batch',
    primaryActionTarget: 'create-batch-form',
    visual: <CreateBatchVisual />,
  },
  {
    id: 3,
    title: 'Add Enhanced Product Information (Optional)',
    summary:
      'Attach richer consumer-facing content like images, nutrition, manufacturing, and packaging data.',
    bullets: [
      'Product type is chosen at the top of batch creation, then extra fields adjust below.',
      'Upload product images and fill nutrition and custom content as needed.',
      'For produce, include harvest date, origin, variety, and shelf-life details.',
    ],
    exampleTitle: 'Example: Enhanced info payload',
    examplePoints: [
      'Manufactured example: calories 120, protein 3g, allergens: milk',
      'Custom content example: "Store in a cool dry place" and brand story text',
      'Produce example: harvest date 2026-02-10, origin Nashik, best-before 30 days',
    ],
    exampleCode:
      'Images: 3 files\nNutrition: { caloriesPerServing: 120, protein: 3 }\nProduce: { origin: "Nashik", bestBeforeDays: 30 }',
    primaryActionLabel: 'Go to Create Batch Form',
    primaryActionTarget: 'create-batch-form',
    visual: <EnhancedInfoVisual />,
  },
  {
    id: 4,
    title: 'Validate Cost and Credits',
    summary:
      'Before submission, verify cost calculation and available credits to avoid failed creation.',
    bullets: [
      'Review total batch creation cost shown in the form.',
      'Ensure available credits are greater than or equal to required cost.',
      'Top up credits from Credit Management if balance is insufficient.',
    ],
    exampleTitle: 'Example: Cost check',
    examplePoints: [
      'Per-unit cost: INR 1.45',
      'Product count: 1,000',
      'Total required: INR 1,450',
      'Available balance: INR 8,000 -> creation allowed',
    ],
    exampleCode: 'INR 1.45 x 1000 = INR 1450',
    primaryActionLabel: 'Open Credit Management',
    primaryActionPath: '/manufacturer/credit-management',
    secondaryActionLabel: 'Open Payment Gateway',
    secondaryActionPath: '/manufacturer/payment',
    visual: <CreditsVisual />,
  },
  {
    id: 5,
    title: 'Generate, Print, and Operate',
    summary:
      'After creation, open batch details to download QR packs, print labels, and configure packaging hierarchy.',
    bullets: [
      'Download QR codes for the full batch and store them securely.',
      'Use Print Pack / Thermal Labels for production and dispatch operations.',
      'Configure case and pallet hierarchy for downstream transfer workflows.',
    ],
    exampleTitle: 'Example: Post-creation execution',
    examplePoints: [
      'Open Batch Details for batch title GHEE01-20260221-B01-500ML-R2',
      'Download all QR files and print thermal labels for packaging line',
      'Set units/case and cases/pallet before dispatch and transfer',
    ],
    exampleCode: 'Units per case: 24\nCases per pallet: 40',
    primaryActionLabel: 'Open Batch Management',
    primaryActionTarget: 'batch-management',
    visual: <PostCreationVisual />,
  },
];

function runGuidePrimaryAction(
  step: GuideStep,
  navigate: ReturnType<typeof useNavigate>,
  handlers: GuideActionHandlers
) {
  if (step.primaryActionTarget === 'batch-title-settings') {
    if (handlers.onOpenBatchTitleSettings) {
      handlers.onOpenBatchTitleSettings();
      return;
    }
    navigate('/products/create-batch#batch-title-settings-section');
    return;
  }

  if (step.primaryActionTarget === 'create-batch-form') {
    if (handlers.onOpenCreateBatchForm) {
      handlers.onOpenCreateBatchForm();
      return;
    }
    navigate('/products/create-batch#create-new-batch-section');
    return;
  }

  if (step.primaryActionTarget === 'batch-management') {
    if (handlers.onOpenBatchManagement) {
      handlers.onOpenBatchManagement();
      return;
    }
    navigate('/products/create-batch#batch-management-section');
    return;
  }

  if (step.primaryActionPath) {
    navigate(step.primaryActionPath);
  }
}

export function BatchCreationStepHelp({
  step,
  onOpenBatchManagement,
  onOpenBatchTitleSettings,
  onOpenCreateBatchForm,
  className,
  defaultExpanded = false,
}: BatchCreationStepHelpProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const stepLabel = typeof step.id === 'number' ? `Step ${step.id}` : step.id;

  return (
    <div
      className={`rounded-xl border border-blue-100 bg-blue-50 p-4 ${className || ''}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-blue-700">
              {stepLabel}
            </span>
            <StatusBadge tone="active">Help</StatusBadge>
          </div>
          <h3 className="mt-2 text-base font-semibold text-slate-900">{step.title}</h3>
          <p className="mt-1 text-sm text-slate-600">{step.summary}</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0 bg-white"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? 'Hide Help' : 'Show Help'}
        </Button>
      </div>

      {expanded ? (
        <div className={`mt-4 ${step.visual ? 'grid grid-cols-1 gap-4 lg:grid-cols-12' : ''}`}>
          <div className={step.visual ? 'lg:col-span-7' : ''}>
            <ul className="space-y-2">
              {step.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-2 text-sm text-slate-700">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                {step.exampleTitle}
              </p>
              <ul className="mt-2 space-y-1.5">
                {step.examplePoints.map((point) => (
                  <li key={point} className="flex gap-2 text-sm text-amber-900">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              {step.exampleCode ? (
                <pre className="mt-3 overflow-x-auto rounded-md border border-amber-300 bg-white px-3 py-2 text-xs text-slate-800">
                  {step.exampleCode}
                </pre>
              ) : null}
            </div>

            {(step.primaryActionLabel || step.secondaryActionLabel) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {step.primaryActionLabel ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() =>
                      runGuidePrimaryAction(step, navigate, {
                        onOpenBatchManagement,
                        onOpenBatchTitleSettings,
                        onOpenCreateBatchForm,
                      })
                    }
                  >
                    {step.primaryActionLabel}
                  </Button>
                ) : null}
                {step.secondaryActionLabel && step.secondaryActionPath ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(step.secondaryActionPath as string)}
                  >
                    {step.secondaryActionLabel}
                  </Button>
                ) : null}
              </div>
            )}
          </div>

          {step.visual ? <div className="lg:col-span-5">{step.visual}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export default function BatchCreationGuideSection({
  onOpenBatchManagement,
  onOpenBatchTitleSettings,
  onOpenCreateBatchForm,
  className,
}: BatchCreationGuideSectionProps) {
  return (
    <section className={className}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-semibold text-slate-900">Batch Creation Guide</h2>
              <StatusBadge tone="active">Inline Help</StatusBadge>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              These same help blocks now live beside the actual batch workflow so users can expand guidance only when they need it.
            </p>
          </div>
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            Use the buttons below to open or close help for each step.
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {batchCreationGuideSteps.map((step) => (
            <BatchCreationStepHelp
              key={step.id}
              step={step}
              defaultExpanded
              onOpenBatchManagement={onOpenBatchManagement}
              onOpenBatchTitleSettings={onOpenBatchTitleSettings}
              onOpenCreateBatchForm={onOpenCreateBatchForm}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
